import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FDPrimaryActionButton, FDStack, FDTitleText, PrimaryActionButton,
} from '@facet-wealth/design';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import fr from 'facet-rest';
import { dataAttrs } from 'Helpers/Testing';
import Uploader from 'Components/Files/Uploader';
import { useSnackbarContext } from 'Context/SnackbarContext';
import useCollectionState from 'Hooks/useCollectionState';
import DataContainer from 'Components/UI/DataContainer';
import { WizardBackButtonRel } from 'Packages/FacetCore/Experimental/RelationshipComponents/WizardBackButtonRel';
import { AttachmentsList } from 'Organisms/AttachmentsList/AttachmentsList';

export const AccountOpeningV2UncommonSituationsUploadStepLayout = ({ refreshCollection, navigateToLink }) => {
  const { onSubmit, resource, saving } = useMetaLayoutContext();
  const [deletingAttachment, setDeletingAttachment] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const {
    loading: loadingAttachments,
    loadingError: loadingAttachmentsError,
    collection: attachmentsCollection,
    setCollection: setAttachments,
  } = useCollectionState({ link: resource.relationship(fr.relationships.relevantAttachments.name).link('related').url() });
  const attachments = useMemo(() => (attachmentsCollection ? attachmentsCollection.resources().list() : []), [attachmentsCollection]);
  const { displayErrorMessage } = useSnackbarContext();

  const onDelete = async attachment => {
    try {
      setDeletingAttachment(true);
      await attachment.delete();

      refreshCollection();

      setAttachments(await resource.relationship(fr.relationships.relevantAttachments.name)?.link('related').get());
      setDeletingAttachment(false);
    } catch {
      setDeletingAttachment(false);
    }
  };

  return (
    <FDStack spacing={3} direction="column">
      <MetaControlField attr="title" />
      <MetaControlField attr="description" />

      <FDStack direction="column" spacing={1}>
        <FDStack direction="row" spacing={2} justifyContent="space-between" wrap>
          <FDTitleText value="Attachments" />
          <Uploader
            url={resource.relationship(fr.relationships.uploadAttachment.name).link('related').url()}
            params={null}
            disableClick={deletingAttachment || uploadingAttachment}
            onStart={() => setUploadingAttachment(true)}
            onFinally={() => setUploadingAttachment(false)}
            onCatch={err => displayErrorMessage(err, err.errors)}
            onSuccess={async () => {
              setAttachments(await resource.relationship(fr.relationships.relevantAttachments.name).link('related').get());
            }}
            displaySuccessToastMessage={false}
          >
            <PrimaryActionButton
              icon="attachFile"
              size="sm"
              label={resource.attr('attachmentButtonText')}
              disabled={deletingAttachment}
              loading={uploadingAttachment}
              {...dataAttrs('upload')}
            />
          </Uploader>
        </FDStack>
        <DataContainer
          loading={loadingAttachments}
          loadingError={loadingAttachmentsError}
          component={AttachmentsList}
          componentProps={{
            attachments,
            onDelete,
            disabled: uploadingAttachment, // Not considering deletingAttachment over here because if an attachment is being deleted, this thing can make that exact attachment appear as being deleted
          }}
        />
      </FDStack>

      <FDStack direction="row" justifyContent="center" alignItems="center" spacing={2}>
        <WizardBackButtonRel resource={resource} overrideOnClick={(_, rel) => navigateToLink(rel.link('related'))} />
        <FDPrimaryActionButton
          {...dataAttrs('continue-btn', resource)}
          label="Save and continue"
          loading={saving}
          onClick={onSubmit}
        />
      </FDStack>
    </FDStack>
  );
};

AccountOpeningV2UncommonSituationsUploadStepLayout.defaultProps = {};

AccountOpeningV2UncommonSituationsUploadStepLayout.propTypes = {
  refreshCollection: PropTypes.func.isRequired,
  navigateToLink: PropTypes.func.isRequired,
};
